import { useEffect } from 'react';

type BottomSpaceInput = {
  /** Высота видимой области – при открытой клавиатуре это не высота окна. */
  viewportHeight: number;
  /** Верх закреплённого элемента в координатах видимой области. */
  elementTop: number;
};

/**
 * Сколько места элемент правда отнимает у содержимого страницы снизу.
 *
 * Вынесено чистой функцией: правило – это арифметика, а не работа с DOM, и проверяется она
 * числами.
 */
export const resolveBottomSpace = ({ viewportHeight, elementTop }: BottomSpaceInput): number =>
  Math.max(0, Math.round(viewportHeight - elementTop));

/**
 * Публикует в CSS-переменную место, которое закреплённый элемент отнимает у страницы снизу.
 *
 * Отличается от {@link useHeightVar} тем, что считает не сам элемент, а <b>занятое им снизу
 * место</b> — вместе с зазором, на который он приподнят над краем. Для полосы разговора это
 * принципиально: она стоит над нижней навигацией и над безопасной зоной, и страница, оставившая
 * место по одной лишь высоте, получает полосу поверх своего нижнего края. В чате туда приходится
 * поле ввода сообщения, и перекрытыми оказываются кнопка отправки и последняя строка текста.
 *
 * Пересчитывается не только на изменение самого элемента, но и на изменение видимой области:
 * открытая клавиатура на Android ужимает webview, и элемент, не поменявший ни высоты, ни отступа,
 * оказывается совсем в другом месте экрана.
 */
export const useBottomSpaceVar = (name: string, node: HTMLElement | null) => {
  useEffect(() => {
    const root = document.documentElement;
    if (!node) {
      root.style.removeProperty(name);
      return undefined;
    }

    const publish = () => {
      const rect = node.getBoundingClientRect();
      // Ориентир — видимая область, а не окно: при открытой клавиатуре они расходятся, и место
      // надо оставлять по той части экрана, которую человек действительно видит.
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      const next = `${resolveBottomSpace({ viewportHeight, elementTop: rect.top })}px`;

      // Пишем только на изменение: лишняя запись стиля документа – лишняя перерисовка.
      if (root.style.getPropertyValue(name) !== next) {
        root.style.setProperty(name, next);
      }
    };

    publish();

    const observer = new ResizeObserver(publish);
    observer.observe(node);
    // Navigation can unmount after this effect measured the bar. Its CSS height variable
    // moves the fixed bar without resizing it, so ResizeObserver cannot clear that reserve.
    let navigationHeight = root.style.getPropertyValue('--bottom-nav-height');
    const placementObserver = new MutationObserver(() => {
      const nextHeight = root.style.getPropertyValue('--bottom-nav-height');
      if (nextHeight !== navigationHeight) {
        navigationHeight = nextHeight;
        publish();
      }
    });
    placementObserver.observe(root, { attributes: true, attributeFilter: ['style'] });
    window.addEventListener('resize', publish);
    window.visualViewport?.addEventListener('resize', publish);
    window.visualViewport?.addEventListener('scroll', publish);

    return () => {
      observer.disconnect();
      placementObserver.disconnect();
      window.removeEventListener('resize', publish);
      window.visualViewport?.removeEventListener('resize', publish);
      window.visualViewport?.removeEventListener('scroll', publish);
      root.style.removeProperty(name);
    };
  }, [name, node]);
};
